import React from 'react';
import { FigmaAnnotationResponse } from '../types/annotation';

interface AnnotationPanelProps {
  annotations: FigmaAnnotationResponse[];
  selectedAnnotationId: number | null;
  onSelectAnnotation: (annotation: FigmaAnnotationResponse) => void;
  onEditAnnotation: (annotation: FigmaAnnotationResponse) => void;
  onDeleteAnnotation: (id: number) => void;
  frameNameById?: Record<string, string>;
  onOpenFrame?: (frameId: string) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Action': '#e74c3c',
  'Navigation': '#3498db',
  'State Change': '#f39c12',
  'Validation': '#9b59b6',
  'API Call': '#2ecc71',
  'Note': '#95a5a6',
};

const AnnotationPanel: React.FC<AnnotationPanelProps> = ({
  annotations,
  selectedAnnotationId,
  onSelectAnnotation,
  onEditAnnotation,
  onDeleteAnnotation,
  frameNameById,
  onOpenFrame,
}) => {
  // Group by node
  const grouped = annotations.reduce<Record<string, FigmaAnnotationResponse[]>>((acc, a) => {
    const key = a.nodeName || a.nodeId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  if (annotations.length === 0) {
    return (
      <div style={{ padding: '16px', color: '#888', fontSize: '13px', textAlign: 'center' }}>
        No annotations yet. Switch to Annotate mode to add pins.
      </div>
    );
  }

  return (
    <div style={{ overflow: 'auto', fontSize: '14px' }}>
      {Object.entries(grouped).map(([nodeKey, nodeAnnotations]) => (
        <div key={nodeKey} style={{ marginBottom: '12px' }}>
          <div
            style={{
              padding: '8px 12px',
              background: '#f0f0f0',
              fontSize: '13px',
              fontWeight: 700,
              color: '#555',
              borderBottom: '1px solid #ddd',
            }}
          >
            {nodeKey}
          </div>
          {nodeAnnotations.map((annotation, idx) => (
            <div
              key={annotation.id}
              onClick={() => onSelectAnnotation(annotation)}
              style={{
                padding: '10px 12px',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                background: selectedAnnotationId === annotation.id ? '#e8f4ff' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: CATEGORY_COLORS[annotation.category] || '#ccc',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 600, fontSize: '15px' }}>{annotation.title}</span>
              </div>
              {annotation.description && (
                <div style={{ color: '#555', fontSize: '13px', marginLeft: '16px', marginBottom: '4px' }}>
                  {annotation.description.length > 80
                    ? annotation.description.substring(0, 80) + '...'
                    : annotation.description}
                </div>
              )}
              {annotation.endpointPath && (
                <div style={{ fontSize: '12px', color: '#4A90D9', marginLeft: '16px' }}>
                  {annotation.endpointMethod?.toUpperCase()} {annotation.endpointPath}
                </div>
              )}
              {annotation.frameId && (
                <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onOpenFrame?.(annotation.frameId);
                    }}
                    style={{
                      fontSize: '12px',
                      color: '#2563eb',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Frame: {frameNameById?.[annotation.frameId] || annotation.frameId}
                  </button>
                </div>
              )}
              {annotation.category === 'Navigation' && annotation.nextFrameId && (
                <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onOpenFrame?.(annotation.nextFrameId);
                    }}
                    style={{
                      fontSize: '12px',
                      color: '#475569',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Next frame: {frameNameById?.[annotation.nextFrameId] || annotation.nextFrameId}
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginLeft: '16px', marginTop: '6px' }}>
                <button
                  onClick={e => { e.stopPropagation(); onEditAnnotation(annotation); }}
                  style={{
                    fontSize: '12px',
                    color: '#1d4ed8',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    padding: '4px 10px',
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={e => { e.stopPropagation(); onDeleteAnnotation(annotation.id); }}
                  style={{
                    fontSize: '12px',
                    color: '#b91c1c',
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    padding: '4px 10px',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default AnnotationPanel;
